/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { RootStore } from 'src/core/stores/RootStore';
import { Model } from 'src/luis/types/Model';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { CompositeEntityStore } from 'src/luis/types/stores/entities/CompositeEntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';
import { VersionedAppId } from 'src/luis/types/VersionedAppId';

/**
 * Gets the model store that fits the given model id and type.
 *
 * @param param0.appId The id of the app that contains the model
 * @param param0.versionId The id of the version that contains the model
 * @param param0.model The model to search for
 */
export const getModelStore = ({
    appId,
    versionId,
    model,
    rootStore
}: VersionedAppId & { model: Omit<Model, 'name'>; rootStore: RootStore }) => {
    const currentAppVersionStore = rootStore.luisAppStoreHouse.findById(appId).appVersionStoreHouse.findById(versionId);
    const { intentStoreHouse, entityStoreHouse } = currentAppVersionStore;

    const foundIntent = intentStoreHouse.findById(model.id);
    const foundEntity = <SimpleEntityStore | CompositeEntityStore>entityStoreHouse.findById(model.id);
    const parentEntity = entityStoreHouse.simpleEntities.find(s => s.getChildById(model.id));

    if (model.typeId === ModelType.Intent) {
        return foundIntent;
    }

    if (foundEntity) {
        return foundEntity;
    }

    return parentEntity.getChildById(model.id);
};

/**
 * Gets the model id that contains the given feature name.
 *
 * @param param0.appId The id of the app that contains the feature
 * @param param0.versionId The id of the version that contains the feature
 * @param param0.featureName The name of the feature
 * @param param0.rootStore The root store to fetch the data from.
 */
export const getModelIdFromModelName = ({
    appId,
    versionId,
    featureName,
    rootStore
}: VersionedAppId & { featureName: string; rootStore: RootStore }): LuisId => {
    const currentAppVersionStore = rootStore.luisAppStoreHouse.findById(appId).appVersionStoreHouse.findById(versionId);
    const { intentStoreHouse, entityStoreHouse, phraseListStoreHouse } = currentAppVersionStore;

    return (
        phraseListStoreHouse.findByName(featureName) ||
        intentStoreHouse.findByName(featureName) ||
        entityStoreHouse.findByName(featureName)
    ).id;
};

/**
 * Gets the model name that contains the given feature id.
 *
 * @param param0.appId The id of the app that contains the feature
 * @param param0.versionId The id of the version that contains the feature
 * @param param0.featureId The id of the feature
 * @param param0.rootStore The root store to fetch the data from.
 */
export const getModelNameFromModelId = ({
    appId,
    versionId,
    featureId,
    rootStore
}: VersionedAppId & { featureId: LuisId; rootStore: RootStore }): string => {
    const currentAppVersionStore = rootStore.luisAppStoreHouse.findById(appId).appVersionStoreHouse.findById(versionId);
    const { intentStoreHouse, entityStoreHouse, phraseListStoreHouse } = currentAppVersionStore;

    return (phraseListStoreHouse.findById(featureId) || intentStoreHouse.findById(featureId) || entityStoreHouse.findById(featureId)).name;
};
